import { fetchDepartments, fetchDepartmentById, createDepartment, updateDepartment, enableDepartment, disableDepartment } from '../api/departmentApi';
import { Department, DepartmentQueryParams, DepartmentListResponse } from '../types/department';
import ApiResponse from '@core/types/apiResponse';
import { ErrorResponse } from '@core/types/errorResponse';
import { UpdateDepartmentRequest } from '../types/departmentRequest';
import { normalizeAllForList } from '@core/utils/listQuery';

/**
 * Service layer for department-related business logic and data processing
 * This layer handles the orchestration of API calls and business rules
 */

/**
 * Fetches departments with business logic for parameter processing and error handling
 * @param params - Query parameters for filtering and pagination
 * @returns Promise<ApiResponse<DepartmentListResponse>>
 */
export const fetchDepartmentsAsync = async (params: DepartmentQueryParams = {}): Promise<ApiResponse<DepartmentListResponse>> => {
  try {
    const normalizedParams = normalizeAllForList(params, ['departmentName', 'departmentId']);
    // Apply business rules for default parameters
    const processedParams = {
      perPage: 10,
      ...normalizedParams,
      // Reset to page 1 when changing filters (business rule)
      page: normalizedParams.departmentName || normalizedParams.departmentId
        ? 1
        : (normalizedParams.page || 1)
    };

    const response = await fetchDepartments(processedParams);

    // Return the response directly as it's already in ApiResponse format
    return response;
  } catch (error) {
    return handleDepartmentError(error);
  }
};

/**
 * Fetches a single department by ID with business logic
 * @param id - Department ID
 * @param scope - Optional scope for scoped endpoint (churchId, districtId, or nationId)
 * @returns Promise<ApiResponse<Department>>
 */
export const fetchDepartmentByIdAsync = async (
  id: number,
  scope?: { churchId?: number; districtId?: number; nationId?: number }
): Promise<ApiResponse<DepartmentListResponse>> => {
  try {
    // Validate ID
    if (!id || id <= 0) {
      throw new Error('Invalid department ID provided');
    }

    const response = await fetchDepartmentById(id, scope);

    // Return the response directly as it's already in ApiResponse format
    return response;
  } catch (error) {
    return handleDepartmentError(error);
  }
};

/**
 * Creates a new department with business logic
 * @param nationId - The ID of the nation
 * @param departmentData - Department creation data
 * @returns Promise<ApiResponse<Department>>
 */
export const createDepartmentAsync = async (nationId: number, departmentData: { name: string; description?: string }): Promise<ApiResponse<any>> => {
  try {
    // Validate input
    if (!nationId || nationId <= 0) {
      throw new Error('Invalid nation ID provided');
    }

    if (!departmentData.name || departmentData.name.trim().length < 2) {
      throw new Error('Department name must be at least 2 characters long');
    }

    const response = await createDepartment(nationId, departmentData);

    // Return the response directly as it's already in ApiResponse format
    return response;
  } catch (error) {
    return handleDepartmentError(error);
  }
};

/**
 * Updates an existing department with business logic
 * @param nationId - The ID of the nation
 * @param departmentId - The ID of the department to update
 * @param departmentData - Department update data
 * @returns Promise<ApiResponse<Department>>
 */
export const updateDepartmentAsync = async (nationId: number, departmentId: number, departmentData: UpdateDepartmentRequest): Promise<ApiResponse<any>> => {
  try {
    // Validate input
    if (!nationId || nationId <= 0) {
      throw new Error('Invalid nation ID provided');
    }

    if (!departmentId || departmentId <= 0) {
      throw new Error('Invalid department ID provided');
    }

    if (!departmentData.name || departmentData.name.trim().length < 2) {
      throw new Error('Department name must be at least 2 characters long');
    }

    const response = await updateDepartment(nationId, departmentId, departmentData);

    // Return the response directly as it's already in ApiResponse format
    return response;
  } catch (error) {
    return handleDepartmentError(error);
  }
};

/**
 * Disables a department (sets it inactive) with input validation.
 * @param nationId - The ID of the nation
 * @param departmentId - The ID of the department to disable
 */
export const disableDepartmentAsync = async (nationId: number, departmentId: number): Promise<ApiResponse<any>> => {
  try {
    if (!nationId || nationId <= 0) {
      throw new Error('Invalid nation ID provided');
    }
    if (!departmentId || departmentId <= 0) {
      throw new Error('Invalid department ID provided');
    }
    return await disableDepartment(nationId, departmentId);
  } catch (error) {
    return handleDepartmentError(error);
  }
};

/**
 * Enables a department (sets it active) with input validation.
 * @param nationId - The ID of the nation
 * @param departmentId - The ID of the department to enable
 */
export const enableDepartmentAsync = async (nationId: number, departmentId: number): Promise<ApiResponse<any>> => {
  try {
    if (!nationId || nationId <= 0) {
      throw new Error('Invalid nation ID provided');
    }
    if (!departmentId || departmentId <= 0) {
      throw new Error('Invalid department ID provided');
    }
    return await enableDepartment(nationId, departmentId);
  } catch (error) {
    return handleDepartmentError(error);
  }
};

/**
 * Processes department data for display (business logic)
 * @param departments - Array of department data
 * @returns Processed department data
 */
export const processDepartmentsForDisplay = (departments: Department[]): Department[] => {
  return departments.map(department => ({
    ...department,
    // Add computed fields for display
    displayName: department.name,
    descriptionDisplay: department.description || 'No description provided',
    // Add any other computed fields as needed
  }));
};

/**
 * Validates department query parameters
 * @param params - Query parameters to validate
 * @returns Validation result with processed parameters
 */
export const validateDepartmentQueryParams = (params: DepartmentQueryParams): {
  isValid: boolean;
  processedParams: DepartmentQueryParams;
  errors: string[]
} => {
  const errors: string[] = [];
  const processedParams = { ...params };

  // Validate pagination
  if (params.perPage && (params.perPage < 1 || params.perPage > 100)) {
    errors.push('Per page must be between 1 and 100');
    processedParams.perPage = Math.max(1, Math.min(100, params.perPage || 10));
  }

  if (params.page && params.page < 1) {
    errors.push('Page must be greater than 0');
    processedParams.page = 1;
  }

  // Validate search parameters
  if (params.departmentName && params.departmentName.length < 2) {
    errors.push('Department name search must be at least 2 characters');
  }

  if (params.departmentId && params.departmentId <= 0) {
    errors.push('Invalid department ID');
  }

  return {
    isValid: errors.length === 0,
    processedParams,
    errors
  };
};

/**
 * Centralized error handling for department operations
 * @param error - Error to handle
 * @returns ApiResponse with error information
 */
const handleDepartmentError = (error: any): ApiResponse<any> => {
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
  const errorResponse: ErrorResponse = {
    statusCode: 500,
    title: 'Department Service Error',
    detail: errorMessage
  };

  return {
    data: null,
    error: errorResponse,
    status: 500,
    isSuccess: () => false,
    isError: () => true,
    getData: () => null,
    getError: () => errorResponse
  };
};